import React from 'react';

interface FloorLevel {
  id: string;
  name: string;
  ordinal: number;
}

interface TopToolbarProps {
  fileName: string;
  proposalStatus: 'draft' | 'pending_review' | 'approved' | 'rejected';
  modificationCount: number;
  pendingChangesCount?: number;
  availableFloors: FloorLevel[];
  currentFloor: string | null;
  onToggleList: () => void;
  onFloorChange: (floorId: string | null) => void;
  onShowPreview: () => void;
  onSaveDraft: () => void;
  onSubmit: () => void;
  onClose?: () => void;
  readonly?: boolean;
  submitDisabled?: boolean;
}

const TopToolbar: React.FC<TopToolbarProps> = ({
  fileName,
  proposalStatus,
  modificationCount,
  pendingChangesCount = 0,
  availableFloors,
  currentFloor,
  onToggleList,
  onFloorChange,
  onShowPreview,
  onSaveDraft,
  onSubmit,
  onClose,
  readonly = false,
  submitDisabled = false,
}) => {
  const getStatusBadge = () => {
    const statusConfig = {
      draft: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: '草稿' },
      pending_review: { bg: 'bg-blue-100', text: 'text-blue-800', label: '待審核' },
      approved: { bg: 'bg-green-100', text: 'text-green-800', label: '已批准' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: '已拒絕' },
    };

    const config = statusConfig[proposalStatus];

    return (
      <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="bg-white shadow-sm border-b flex-shrink-0">
      {/* 第一行:標題與主要資訊 */}
      <div className="px-6 py-3 flex items-center justify-between border-b border-gray-200">
        <div className="flex items-center space-x-4">
          {/* 關閉按鈕 */}
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors text-gray-700 hover:text-gray-900"
              title="關閉編輯器"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}

          {/* 列表切換按鈕 */}
          <button
            onClick={onToggleList}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            title="顯示/隱藏 Feature 列表"
          >
            <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* 標題與狀態 */}
          <div>
            <h1 className="text-lg font-bold text-gray-900">IMDF 屬性編輯器</h1>
            <p className="text-sm text-gray-600">{fileName}</p>
          </div>

          {/* 狀態標籤 */}
          {getStatusBadge()}

          {/* 修改計數 */}
          <div className="flex items-center space-x-3 text-sm">
            <div className="flex items-center text-green-600">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span>{modificationCount} 個已保存修改</span>
            </div>

            {pendingChangesCount > 0 && (
              <div className="flex items-center text-orange-600">
                <span className="inline-block w-2 h-2 bg-orange-400 rounded-full mr-2"></span>
                <span>{pendingChangesCount} 個待保存更改</span>
              </div>
            )}
          </div>
        </div>

        {/* 操作按鈕 */}
        <div className="flex items-center space-x-3">
          <button
            onClick={onShowPreview}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors text-sm font-medium"
          >
            預覽修改
          </button>

          {!readonly && proposalStatus === 'draft' && (
            <>
              <button
                onClick={onSaveDraft}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                儲存草稿
              </button>

              <button
                onClick={onSubmit}
                disabled={submitDisabled}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                title={submitDisabled ? '請先保存所有更改再提交審核' : '提交審核'}
              >
                提交審核
              </button>
            </>
          )}
        </div>
      </div>

      {/* 第二行:樓層篩選 */}
      <div className="px-6 py-3 flex items-center space-x-3">
        <span className="text-sm font-medium text-gray-700 flex-shrink-0">樓層篩選:</span>
        <div className="flex items-center space-x-2 overflow-x-auto flex-1">
          {/* 全部樓層按鈕 */}
          <button
            onClick={() => onFloorChange(null)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
              currentFloor === null
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            全部
          </button>

          {/* 各樓層按鈕 */}
          {availableFloors.map((floor) => (
            <button
              key={floor.id}
              onClick={() => onFloorChange(floor.id)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                currentFloor === floor.id
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {floor.name}
            </button>
          ))}

          {availableFloors.length === 0 && (
            <span className="text-sm text-gray-500 italic">無樓層資訊</span>
          )}
        </div>

        {/* 目前樓層提示 */}
        {currentFloor && (
          <div className="flex items-center text-sm text-purple-600 flex-shrink-0 ml-4">
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>
              已篩選: {availableFloors.find(f => f.id === currentFloor)?.name || '未知樓層'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopToolbar;
